import {useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {HeadingCarousel} from "@/components";
import {Carousel} from "@/components/Carousel";
import {useGetBigWinners} from "@/hooks/gamesList/useGetBigWinners";
import {useAuthStore} from "@/store/useAuth.store";
import {formatAmountSuffix} from "@/util/balance.util";
import {formatCurrency} from "@/util/formatCurrency";
import betbyCarouselIcon from "../../../../../assets/images/betby_carousel_icon_light.png";
import fallbackGameImage from "../../../../../assets/images/fallback-game.svg";
import styles from "./breaking-league.styles.module.scss";

const skeletonKeys = [
  "hb-skel-0",
  "hb-skel-1",
  "hb-skel-2",
  "hb-skel-3",
  "hb-skel-4",
  "hb-skel-5",
  "hb-skel-6",
  "hb-skel-7",
];

export const BreakingLeague = () => {
  const {t} = useTranslation();

  const {winners, isPending, isFetching} = useGetBigWinners();
  const [carouselApi, setCarouselApi] = useState(null);
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const handleGameClick = useCallback(
    (provider, gameId) => {
      if (authStatus === "authenticated") {
        if (provider === "betby") {
          navigate("/sports");
        } else {
          navigate(`/games/${provider}/${gameId}`);
        }
      } else {
        handleShowAuthModal();
      }
    },
    [authStatus, navigate, handleShowAuthModal]
  );

  const showSkeletons = isPending || isFetching;

  const duplicatedWinners =
    winners.length > 0
      ? Array(Math.ceil(32 / winners.length))
          .fill(winners)
          .flat()
          .slice(0, 32)
      : [];

  const winnerCards =
    showSkeletons || winners.length === 0
      ? skeletonKeys.map(key => (
          <div
            key={key}
            className={styles.skeletonCard}
          >
            <div className={styles.skeletonAvatar} />
            <div className={styles.skeletonLine} />
            <div className={styles.skeletonLineShort} />
            <div className={styles.skeletonLineMedium} />
          </div>
        ))
      : duplicatedWinners.map((winner, id) => (
          <div
            key={`${winner.user_id || winner.user_alias}-${winner.game_id}-${winner.amount_float}-${id}`}
            className={styles.card}
            onClick={() => {
              if (winner.game_id && winner.provider) {
                handleGameClick(winner.provider, winner.game_id);
              }
            }}
          >
            <div className={styles.gameCard}>
              <img
                src={winner.provider === "betby" ? betbyCarouselIcon : winner.icon || fallbackGameImage}
                alt={winner.game_name || winner.user_alias || "game"}
                className={styles.avatarImg}
                onError={e => {
                  e.target.onerror = null;
                  e.target.src = fallbackGameImage;
                }}
              />
            </div>
            <div className={styles.infoContainer}>
              <p className={styles.card__username}>@{winner.user_alias}</p>
              <p className={styles.card__prize}>
                {formatAmountSuffix(winner.amount_float)} {formatCurrency(winner.currency)}
              </p>
            </div>
          </div>
        ));

  return (
    <div
      id="breakingLeague"
      className={styles.wrapper}
    >
      <HeadingCarousel
        title={t("titleBreakingLeague")}
        subtitle={t("gamesSubTitle")}
        icon="😎"
        onPrev={carouselApi?.scrollPrev}
        onNext={carouselApi?.scrollNext}
      />
      <Carousel
        onInit={setCarouselApi}
        renderItems={winnerCards}
      />
    </div>
  );
};
